import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";

declare const __APP_VERSION__: string | undefined;

const APP_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined;

let sentryInitialized = false;

export function initSentry() {
  if (sentryInitialized) {
    return;
  }

  if (!import.meta.env.PROD) {
    if (import.meta.env.DEV) {
      console.info(
        "[Sentry] Skipping initialization in development mode. Set VITE_SENTRY_FORCE_INIT=true to override."
      );
    }
    return;
  }

  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn("[Sentry] VITE_SENTRY_DSN missing; Sentry not initialized.");
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    release:
      import.meta.env.VITE_SENTRY_RELEASE ||
      (APP_VERSION ? `churchhub-frontend@${APP_VERSION}` : undefined),
    integrations: [browserTracingIntegration(), replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    debug: import.meta.env.DEV,
    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
      }
      return event;
    },
  });

  sentryInitialized = true;
  console.info(
    `[Sentry] Initialized successfully (${
      import.meta.env.VITE_SENTRY_ENVIRONMENT || "production"
    })`
  );
  Sentry.captureMessage("✅ Sentry initialized in production", "info");
}

export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, { extra: context });
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.captureMessage(message, level);
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  Sentry.setUser(user);
}

export function clearUserContext() {
  Sentry.setUser(null);
}
